/**
 * DEV-2826. Бейджи раздела «Коммуникации» в левом меню: что ждёт реакции менеджера.
 *
 * - Чаты — чаты с непрочитанными входящими в списке по умолчанию: тем, кто разбирает
 *   чаты по категориям, — клиенты, агенты и «другое»; остальным — только клиенты. Только те,
 *   что смотрящий может отметить прочитанными (DEV-2831): чужой личный Telegram не в счёт.
 * - Звонки — клиенты и номера, которые не дозвонились и которым ещё не перезвонили.
 * - Встречи — встречи, где предложения ИИ в карточку ещё не применены и не отклонены.
 *
 * Счётчик раздела без доступа не запрашивается. Сбой одного счётчика оставляет
 * его прежнее число и не трогает остальные: бейдж — подсказка, не данные.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { usePermissions } from '@/features/auth/composables/usePermissions'
import { useSectionsStore } from '@/features/auth/stores/sections.store'
import { getMeetingsStats } from '@/features/videomeetings/api/videomeetings.api'

import { communicationsApi } from '../api/communications.api'

export const useCommunicationsBadgesStore = defineStore('communicationsBadges', () => {
  const unreadChats = ref(0)
  const awaitingCallback = ref(0)
  const pendingSuggestions = ref(0)

  const sections = useSectionsStore()
  const { canSortChats } = usePermissions()

  const settle = async (
    enabled: boolean,
    load: () => Promise<number>,
    apply: (n: number) => void,
  ) => {
    if (!enabled) return
    try {
      apply(await load())
    } catch (err) {
      console.warn('[communicationsBadges] counter failed:', err)
    }
  }

  async function refresh(): Promise<void> {
    await Promise.all([
      settle(
        sections.hasSection('chats'),
        async () => {
          const c = await communicationsApi.countUnreadChats({ markableOnly: true })
          return canSortChats.value ? c.client + c.agent + c.other : c.client
        },
        (n) => (unreadChats.value = n),
      ),
      settle(
        sections.hasSection('calls'),
        () => communicationsApi.countAwaitingCallback(),
        (n) => (awaitingCallback.value = n),
      ),
      settle(
        sections.hasSection('videomeetings'),
        async () => (await getMeetingsStats({})).pendingSuggestions,
        (n) => (pendingSuggestions.value = n),
      ),
    ])
  }

  return { unreadChats, awaitingCallback, pendingSuggestions, refresh }
})
